import { useQuery, useQueryClient } from "@tanstack/react-query";

import { logError } from "../../../utils/error";
import { useAuth } from "../AuthContext";
const STORAGE_KEY = "enlighten_selected_groups";

interface SelectedGroupsState {
  selectedGroups: string[];
  permittedEmails: string[] | null;
}

const calculatePermittedEmails = (
  analysisGroups: Record<string, string[]> | null,
  selectedGroups: string[]
): string[] | null => {
  if (!analysisGroups || selectedGroups.length === 0) {
    return null;
  }

  const uniqueEmails = [...new Set(
    selectedGroups.flatMap(groupName => analysisGroups[groupName] || [])
  )].map(email => email.toLowerCase());

  return uniqueEmails.length > 0 ? uniqueEmails : null;
};

const getStoredSelectedGroups = (): string[] | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored);
  } catch (error) {
    logError("Error parsing stored selected groups", error);
    return null;
  }
};

const setStoredSelectedGroups = (groups: string[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
  } catch (error) {
    logError("Error storing selected groups", error);
  }
};

export const useSelectedGroups = () => {
  const { analysisGroups } = useAuth();
  const queryClient = useQueryClient();

  const availableGroups = analysisGroups ? Object.keys(analysisGroups) : [];

  const { data: selectedGroupsState, isLoading } = useQuery({
    queryKey: ["selectedGroups"],
    queryFn: (): SelectedGroupsState => {
      if (!analysisGroups) {
        return { selectedGroups: [], permittedEmails: null };
      }

      const storedGroups = getStoredSelectedGroups();
      let selectedGroups = storedGroups?.filter(group => availableGroups.includes(group)) || [];

      // If no valid stored groups, fall back to all available groups
      if (selectedGroups.length === 0) {
        selectedGroups = availableGroups;
      }

      return {
        selectedGroups,
        permittedEmails: calculatePermittedEmails(analysisGroups, selectedGroups)
      };
    },
    staleTime: 5000,
    gcTime: 5000
  });

  const updateSelectedGroups = (newSelectedGroups: string[]): void => {
    const validGroups = newSelectedGroups.filter(group => availableGroups.includes(group));

    if (validGroups.length === 0) {
      return;
    }

    const newState: SelectedGroupsState = {
      selectedGroups: validGroups,
      permittedEmails: calculatePermittedEmails(analysisGroups, validGroups)
    };

    queryClient.setQueryData(["selectedGroups"], newState);
    setStoredSelectedGroups(validGroups);
  };

  const hasMultipleGroups = availableGroups.length > 1;
  const selectedGroups = selectedGroupsState?.selectedGroups || [];

  return {
    selectedGroups,
    permittedEmails: selectedGroupsState?.permittedEmails || null,
    availableGroups,
    updateSelectedGroups,
    isLoading,
    isFiltered: hasMultipleGroups && selectedGroups.length < availableGroups.length,
    hasMultipleGroups
  };
};
